import { useCallback, useEffect, useMemo, useState } from "react";import { Copy, LifeBuoy, MessagesSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { ChatThread } from "@/components/chat/ChatThread";
import { navForRole } from "@/components/roleNav";
import { useAuth } from "@/context/AuthContext";
import { chatService } from "@/services/chatService";
import { managerService } from "@/services/paymentService";
import { errorMessage } from "@/services/apiClient";

const POLL_MS = 10000;

export default function InboxPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const nav = useMemo(() => navForRole(user?.role), [user?.role]);
  const [statuses, setStatuses] = useState([]);
  const [status, setStatus] = useState("");
  const [bucket, setBucket] = useState("active");
  const [kind, setKind] = useState("client");
  const [staffOpen, setStaffOpen] = useState(false);
  const [staffForm, setStaffForm] = useState({ subject: "", message: "" });
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ client_id: "", subject: "", message: "" });

  useEffect(() => {
    chatService.meta().then((m) => setStatuses(m.statuses)).catch(() => setStatuses([]));
  }, []);

  const loadList = useCallback(async () => {
    try {
      const data = await chatService.conversations({ status, search, bucket, kind });
      setItems(data);
      return data;
    } catch {
      return [];
    }
  }, [status, search, bucket, kind]);

  const openConversation = useCallback(async (id) => {
    try {
      setActive(await chatService.conversation(id));
      loadList();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [loadList]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadList();
      setActive((current) => {
        if (current) chatService.conversation(current.id).then(setActive).catch(() => {});
        return current;
      });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [loadList]);

  const startNew = async () => {
    setCreating((v) => !v);
    if (clients.length === 0) {
      try {
        setClients(await managerService.clients({}));
      } catch {
        setClients([]);
      }
    }
  };

  const send = async (body) => {
    try {
      await chatService.send(active.id, body);
      await openConversation(active.id);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const statusOptions = useMemo(() => statuses.filter((s) => (bucket === "archived"
    ? ["resolvido", "fechado"].includes(s.value)
    : !["resolvido", "fechado"].includes(s.value))), [statuses, bucket]);

  const isRequester = user?.role === "manager" || user?.role === "agent";

  const createStaff = async (e) => {
    e.preventDefault();
    try {
      const created = await chatService.createStaff({
        subject: staffForm.subject.trim(),
        message: staffForm.message.trim(),
      });
      setStaffOpen(false);
      setStaffForm({ subject: "", message: "" });
      await loadList();
      openConversation(created.id);
      toast.success("Pedido enviado ao suporte");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const copyClientId = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("ID do cliente copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const changeStatus = async (value) => {
    try {
      await chatService.setStatus(active.id, value);
      await openConversation(active.id);
      toast.success("Estado atualizado");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const created = await chatService.create({
        client_id: form.client_id,
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setForm({ client_id: "", subject: "", message: "" });
      setCreating(false);
      await loadList();
      openConversation(created.id);
      toast.success("Conversa iniciada");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <AppShell nav={nav} title="Mensagens" subtitle="Atendimento aos seus clientes — apenas texto, histórico de 30 dias">
      <div data-testid="inbox-page" className="space-y-6">
        <div className="flex gap-2">
          {[{ k: "active", label: "Ativas" }, { k: "archived", label: "Arquivadas" }].map((tab) => (
            <button
              key={tab.k}
              data-testid={`inbox-tab-${tab.k}`}
              onClick={() => { setKind("client"); setBucket(tab.k); setStatus(""); setActive(null); }}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
                kind === "client" && bucket === tab.k
                  ? "bg-[#00A884] text-white"
                  : "border border-[#22252F] text-[#94A3B8] hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
          <button
            data-testid="inbox-tab-staff"
            onClick={() => { setKind("staff"); setBucket(""); setStatus(""); setActive(null); }}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              kind === "staff" ? "bg-[#2563EB] text-white" : "border border-[#22252F] text-[#94A3B8] hover:text-white"
            }`}
          >
            Equipa
          </button>
          {isRequester ? (
            <button
              data-testid="talk-to-support-button"
              onClick={() => { setKind("staff"); setStaffForm({ subject: "", message: "" }); setStaffOpen(true); }}
              className="inline-flex items-center gap-2 rounded-full border border-[#2563EB]/40 bg-[#2563EB]/10 px-4 py-1.5 text-sm text-white"
            >
              <LifeBuoy className="h-4 w-4" /> Falar com suporte
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            data-testid="inbox-status-filter"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-[#22252F] bg-[#0E1015] px-3 py-2 text-sm text-white"
          >
            <option value="">Todos os estados</option>
            {statusOptions.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <input
            data-testid="inbox-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Procurar cliente ou assunto"
            className="rounded-md border border-[#22252F] bg-[#0E1015] px-3 py-2 text-sm text-white"
          />
          <button
            data-testid="inbox-new-button"
            onClick={startNew}
            className="flex items-center gap-2 rounded-md border border-[#2563EB]/40 bg-[#2563EB]/10 px-4 py-2 text-sm text-white"
          >
            <Plus className="h-4 w-4" /> Nova conversa
          </button>
        </div>

        {creating ? (
          <form onSubmit={create} data-testid="inbox-new-form" className="space-y-3 rounded-md border border-[#22252F] bg-[#0E1015] p-4">
            <select
              data-testid="inbox-new-client"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full rounded-md border border-[#22252F] bg-[#12141A] px-3 py-2 text-sm text-white"
            >
              <option value="">Selecione o cliente</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name} {c.email ? `· ${c.email}` : ""}</option>
              ))}
            </select>
            <input
              data-testid="inbox-new-subject"
              value={form.subject}
              maxLength={120}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Assunto"
              className="w-full rounded-md border border-[#22252F] bg-[#12141A] px-3 py-2 text-sm text-white"
            />
            <textarea
              data-testid="inbox-new-message"
              value={form.message}
              rows={3}
              maxLength={600}
              onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, 600) })}
              placeholder="Mensagem (máx. 600 caracteres)"
              className="w-full resize-none rounded-md border border-[#22252F] bg-[#12141A] px-3 py-2 text-sm text-white"
            />
            <button
              type="submit"
              data-testid="inbox-new-submit"
              disabled={!form.client_id || !form.subject.trim() || !form.message.trim()}
              className="rounded-md bg-[#2563EB] px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              Iniciar conversa
            </button>
          </form>
        ) : null}

        {staffOpen ? (
          <form onSubmit={createStaff} data-testid="staff-request-form"
                className="space-y-3 rounded-md border border-[#2563EB]/40 bg-[#0E1015] p-4">
            <p className="text-xs text-[#94A3B8]">
              Pedido interno para o Supervisor de suporte (ex.: "cliente X — adicionar saldo" ou
              "aumentar nível de margem"). Se não houver Supervisor de suporte ativo, vai para o Admin.
            </p>
            <input data-testid="staff-request-subject" value={staffForm.subject} maxLength={120}
                   onChange={(e) => setStaffForm({ ...staffForm, subject: e.target.value })}
                   placeholder="Assunto (ex.: Cliente Ana — adicionar saldo)"
                   className="w-full rounded-md border border-[#22252F] bg-[#12141A] px-3 py-2 text-sm text-white" />
            <textarea data-testid="staff-request-message" value={staffForm.message} rows={3} maxLength={600}
                      onChange={(e) => setStaffForm({ ...staffForm, message: e.target.value.slice(0, 600) })}
                      placeholder="Descreva o pedido (máx. 600 caracteres)"
                      className="w-full resize-none rounded-md border border-[#22252F] bg-[#12141A] px-3 py-2 text-sm text-white" />
            <button type="submit" data-testid="staff-request-submit"
                    disabled={!staffForm.subject.trim() || !staffForm.message.trim()}
                    className="rounded-md bg-[#2563EB] px-4 py-2 text-sm font-medium text-white disabled:opacity-40">
              Enviar pedido
            </button>
          </form>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <div data-testid="inbox-list" className="max-h-[32rem] space-y-2 overflow-y-auto pr-1">
            {items.length === 0 ? (
              <p className="rounded-md border border-[#22252F] bg-[#0E1015] p-4 text-sm text-[#94A3B8]">
                Sem conversas para os filtros atuais.
              </p>
            ) : null}
            {items.map((c) => (
              <button
                key={c.id}
                data-testid={`inbox-conversation-${c.id}`}
                onClick={() => openConversation(c.id)}
                className={`w-full rounded-md border px-3 py-3 text-left text-sm transition-colors ${
                  active?.id === c.id ? "border-[#2563EB]/50 bg-[#2563EB]/10" : "border-[#22252F] bg-[#0E1015] hover:bg-[#151821]"
                }`}
              >
                <p className="flex items-center gap-2 truncate font-medium text-white">
                  <MessagesSquare className="h-4 w-4 text-[#60A5FA]" /> {c.kind === "staff" ? c.requester_name : c.client_name}
                </p>
                <p className="truncate text-xs text-[#94A3B8]">
                  {c.kind === "staff" ? `${c.requester_role === "manager" ? "Gestor" : "Agente"} · ` : ""}{c.subject}
                </p>
                <p className="mt-1 truncate text-xs text-[#64748B]">{c.last_message_preview}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-[#60A5FA]">
                  {c.status_label}
                  {c.unread > 0 ? ` · ${c.unread} nova(s)` : ""}
                </p>
              </button>
            ))}
          </div>

          {active ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#22252F] bg-[#0E1015] px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-white" data-testid="inbox-active-client">
                    {active.kind === "staff" ? `${active.requester_name} (equipa)` : active.client_name}
                  </p>
                  <p className="text-xs text-[#94A3B8]">{active.subject}</p>
                  {active.kind === "staff" ? null : (
                  <p className="mt-1 flex items-center gap-2 font-mono text-[11px] text-[#64748B]">
                    ID: <span data-testid="inbox-client-id">{active.client_id}</span>
                    <button onClick={() => copyClientId(active.client_id)} data-testid="inbox-copy-client-id"
                            title="Copiar ID do cliente" className="transition-colors hover:text-white">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => navigate("/clients", { state: { id: active.client_id } })}
                            data-testid="inbox-open-profile" className="underline transition-colors hover:text-white">
                      abrir perfil
                    </button>
                  </p>
                  )}
                </div>
                <select
                  data-testid="inbox-status-select"
                  value={active.status}
                  onChange={(e) => changeStatus(e.target.value)}
                  className="rounded-md border border-[#22252F] bg-[#12141A] px-3 py-2 text-sm text-white"
                >
                  {statuses.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <ChatThread conversation={active} staffView onSend={send} />
            </div>
          ) : (
            <div className="rounded-md border border-[#22252F] bg-[#0E1015] p-6 text-sm text-[#94A3B8]">
              Selecione uma conversa para responder.
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
